import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LayoutDashboard, LogOut, Menu, Package, Settings, SlidersHorizontal, Sparkles, User, X } from 'lucide-react';

const STORAGE_KEY = 'sidebar-collapsed';

function CollapsedFlyout({
  label,
  badge,
  hoverGroup,
}: {
  label: string;
  badge?: string;
  hoverGroup?: 'logout';
}) {
  const hover =
    hoverGroup === 'logout'
      ? 'group-hover/logout:visible group-hover/logout:translate-x-0 group-hover/logout:opacity-100'
      : 'group-hover:visible group-hover:translate-x-0 group-hover:opacity-100';

  return (
    <span
      role="tooltip"
      className={`pointer-events-none absolute left-[calc(100%+0.5rem)] top-1/2 z-[60] flex -translate-y-1/2 items-center gap-2 whitespace-nowrap rounded-xl border border-slate-600/80 bg-slate-950 px-3 py-2 text-sm font-medium text-white shadow-xl shadow-black/40 opacity-0 invisible translate-x-1 transition-all duration-200 ${hover} before:absolute before:right-full before:top-1/2 before:-translate-y-1/2 before:border-[6px] before:border-transparent before:border-r-slate-950`}
    >
      {label}
      {badge && (
        <span className="rounded-md bg-slate-800 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
          {badge}
        </span>
      )}
    </span>
  );
}

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { user, userEmail, userRole, signOut } = useAuth();

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(desktopCollapsed));
    } catch {
      /* ignore */
    }
  }, [desktopCollapsed]);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    await signOut();
    setIsLoggingOut(false);
  };

  const isActive = (path: string) => location.pathname === path;
  const showFlyouts = desktopCollapsed;

  return (
    <>
      <button
        type="button"
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600 shadow-lg"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
      >
        {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
      </button>

      <aside
        className={`${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } ${
          desktopCollapsed ? 'lg:w-[4.25rem]' : 'lg:w-64'
        } fixed inset-y-0 left-0 z-50 w-64 shrink-0 bg-gradient-to-b from-slate-800 to-slate-900 shadow-xl transform transition-[width,transform] duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${
          showFlyouts ? 'lg:overflow-visible' : ''
        }`}
      >
        <div
          className={`flex flex-col h-full min-h-0 ${
            showFlyouts ? 'lg:overflow-visible' : 'overflow-hidden'
          }`}
        >
          <div
            className={`flex items-center gap-2 h-16 px-3 border-b border-slate-700/80 shrink-0 ${
              desktopCollapsed ? 'lg:flex-col lg:justify-center lg:gap-1.5 lg:py-3 lg:h-auto' : ''
            }`}
          >
            <div
              className={`flex flex-1 items-center min-w-0 ${
                desktopCollapsed ? 'lg:flex-none lg:justify-center' : ''
              }`}
            >
              <span
                className={`text-xl font-semibold text-white whitespace-nowrap ${
                  desktopCollapsed ? 'lg:hidden' : 'ml-1 truncate'
                }`}
              >
                brix<span className="text-fuchsia-300">ECOM</span>
              </span>
              <span
                className={`hidden text-lg font-semibold text-white ${
                  desktopCollapsed ? 'lg:inline' : ''
                }`}
              >
                b<span className="text-fuchsia-300">E</span>
              </span>
            </div>

            <button
              type="button"
              onClick={() => setSidebarOpen(false)}
              className="text-slate-300 hover:text-white transition-colors lg:hidden shrink-0"
              aria-label="Close menu"
            >
              <X className="h-6 w-6" />
            </button>

            <button
              type="button"
              onClick={() => setDesktopCollapsed((c) => !c)}
              className="hidden lg:inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-slate-300 backdrop-blur-sm transition-all duration-200 hover:border-fuchsia-400/30 hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-fuchsia-400/50"
              aria-label={desktopCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-expanded={!desktopCollapsed}
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>

          <nav
            className={`flex-1 px-2 py-4 ${
              showFlyouts ? 'lg:overflow-y-auto lg:overflow-x-visible' : 'overflow-y-auto overflow-x-hidden'
            }`}
          >
            <a
              href="/dashboard"
              className={`group relative flex items-center py-2.5 text-sm font-medium rounded-xl transition-all duration-200 ${
                desktopCollapsed ? 'lg:justify-center lg:px-0 lg:mx-auto lg:w-10' : 'px-3'
              } ${
                isActive('/dashboard')
                  ? 'bg-white/20 text-white shadow-md ring-1 ring-white/10'
                  : 'text-slate-200 hover:bg-white/10 hover:text-white'
              }`}
              onClick={(e) => {
                e.preventDefault();
                navigate('/dashboard');
                setSidebarOpen(false);
              }}
            >
              <LayoutDashboard
                className={`h-5 w-5 shrink-0 ${desktopCollapsed ? 'lg:mr-0' : 'mr-3'}`}
              />
              <span className={`flex-1 truncate ${desktopCollapsed ? 'lg:hidden' : ''}`}>
                Dashboard
              </span>
              {showFlyouts && (
                <span className="hidden lg:contents">
                  <CollapsedFlyout label="Dashboard" />
                </span>
              )}
            </a>

            <a
              href="/products"
              className={`group relative flex items-center py-2.5 text-sm font-medium rounded-xl transition-all duration-200 mt-1 ${
                desktopCollapsed ? 'lg:justify-center lg:px-0 lg:mx-auto lg:w-10' : 'px-3'
              } ${
                isActive('/products')
                  ? 'bg-white/20 text-white shadow-md ring-1 ring-white/10'
                  : 'text-slate-200 hover:bg-white/10 hover:text-white'
              }`}
              onClick={(e) => {
                e.preventDefault();
                navigate('/products');
                setSidebarOpen(false);
              }}
            >
              <Package
                className={`h-5 w-5 shrink-0 ${desktopCollapsed ? 'lg:mr-0' : 'mr-3'}`}
              />
              <span className={`flex-1 truncate ${desktopCollapsed ? 'lg:hidden' : ''}`}>
                Products
              </span>
              {showFlyouts && (
                <span className="hidden lg:contents">
                  <CollapsedFlyout label="Products" />
                </span>
              )}
            </a>

            <a
              href="/manage-slider"
              className={`group relative flex items-center py-2.5 text-sm font-medium rounded-xl transition-all duration-200 mt-1 ${
                desktopCollapsed ? 'lg:justify-center lg:px-0 lg:mx-auto lg:w-10' : 'px-3'
              } ${
                isActive('/manage-slider')
                  ? 'bg-white/20 text-white shadow-md ring-1 ring-white/10'
                  : 'text-slate-200 hover:bg-white/10 hover:text-white'
              }`}
              onClick={(e) => {
                e.preventDefault();
                navigate('/manage-slider');
                setSidebarOpen(false);
              }}
            >
              <SlidersHorizontal
                className={`h-5 w-5 shrink-0 ${desktopCollapsed ? 'lg:mr-0' : 'mr-3'}`}
              />
              <span className={`flex-1 truncate ${desktopCollapsed ? 'lg:hidden' : ''}`}>
                Manage Slider
              </span>
              {showFlyouts && (
                <span className="hidden lg:contents">
                  <CollapsedFlyout label="Manage Slider" />
                </span>
              )}
            </a>

            <a
              href="/manage-featured-product-category"
              className={`group relative flex items-center py-2.5 text-sm font-medium rounded-xl transition-all duration-200 mt-1 ${
                desktopCollapsed ? 'lg:justify-center lg:px-0 lg:mx-auto lg:w-10' : 'px-3'
              } ${
                isActive('/manage-featured-product-category')
                  ? 'bg-white/20 text-white shadow-md ring-1 ring-white/10'
                  : 'text-slate-200 hover:bg-white/10 hover:text-white'
              }`}
              onClick={(e) => {
                e.preventDefault();
                navigate('/manage-featured-product-category');
                setSidebarOpen(false);
              }}
            >
              <Sparkles
                className={`h-5 w-5 shrink-0 ${desktopCollapsed ? 'lg:mr-0' : 'mr-3'}`}
              />
              <span className={`flex-1 truncate ${desktopCollapsed ? 'lg:hidden' : ''}`}>
                Featured
              </span>
              {showFlyouts && (
                <span className="hidden lg:contents">
                  <CollapsedFlyout label="Featured" />
                </span>
              )}
            </a>

            <a
              href="/site-settings"
              className={`group relative flex items-center py-2.5 text-sm font-medium rounded-xl transition-all duration-200 mt-1 ${
                desktopCollapsed ? 'lg:justify-center lg:px-0 lg:mx-auto lg:w-10' : 'px-3'
              } ${
                isActive('/site-settings')
                  ? 'bg-white/20 text-white shadow-md ring-1 ring-white/10'
                  : 'text-slate-200 hover:bg-white/10 hover:text-white'
              }`}
              onClick={(e) => {
                e.preventDefault();
                navigate('/site-settings');
                setSidebarOpen(false);
              }}
            >
              <Settings
                className={`h-5 w-5 shrink-0 ${desktopCollapsed ? 'lg:mr-0' : 'mr-3'}`}
              />
              <span className={`flex-1 truncate ${desktopCollapsed ? 'lg:hidden' : ''}`}>
                Site Settings
              </span>
              {showFlyouts && (
                <span className="hidden lg:contents">
                  <CollapsedFlyout label="Site Settings" />
                </span>
              )}
            </a>
          </nav>

          <div
            className={`p-3 border-t border-slate-700/80 shrink-0 ${
              desktopCollapsed ? 'lg:px-2' : ''
            }`}
          >
            <div
              className={`flex items-center rounded-xl p-1.5 ${
                desktopCollapsed ? 'lg:flex-col lg:gap-2 lg:items-center' : ''
              }`}
            >
              <div className="group relative shrink-0">
                <div className="w-9 h-9 bg-gradient-to-br from-fuchsia-500/30 to-indigo-500/30 ring-1 ring-white/15 rounded-full flex items-center justify-center transition-colors group-hover:ring-fuchsia-400/40">
                  <User className="h-5 w-5 text-white" />
                </div>
                {showFlyouts && (
                  <CollapsedFlyout
                    label={userEmail || user?.email || 'Account'}
                    badge={userRole ?? undefined}
                  />
                )}
              </div>
              <div className={`ml-2.5 flex-1 min-w-0 ${desktopCollapsed ? 'lg:hidden' : ''}`}>
                <p className="text-sm font-medium text-white truncate">
                  {userEmail || user?.email || 'User'}
                </p>
                <p className="text-xs text-slate-400 capitalize">{userRole}</p>
              </div>
              <button
                type="button"
                onClick={handleLogout}
                disabled={isLoggingOut}
                className={`group/logout relative text-slate-300 p-2 rounded-lg transition-colors shrink-0 ${
                  isLoggingOut
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-white/10 hover:text-white'
                }`}
              >
                {isLoggingOut ? (
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                ) : (
                  <LogOut className="h-4 w-4" />
                )}
                {showFlyouts && (
                  <span className="hidden lg:contents">
                    <CollapsedFlyout label="Sign out" hoverGroup="logout" />
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </aside>

      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden
        />
      )}
    </>
  );
};

export default Sidebar;
